'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { DomainTree } from '@terra/ui-design-system';
import { useTreeFilterParams } from '@terra/ui-design-system';
import { useAppTargetId } from '../store/projectStore';
import { useApplicationDomainTree } from '../hooks/api/useApplicationDomainTree';
import type { MainContentTab } from '../store/layoutStore';
import { type AssetType } from '../store/tab/assetsTabScopedFilterStore';
import {
  isDomainTreeFilterTab,
  resolveActiveTabDomainTreeQueryOptions,
} from '../utils/domainTreeQueryOptions';

export {
  DOMAIN_TREE_FILTER_TABS,
  isDomainTreeFilterTab,
} from '../utils/domainTreeQueryOptions';

type SharedDomainTreeContextValue = {
  domainTree: DomainTree | null;
  generateUrlPatterns: (selectedNodeIds: string[]) => string[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

const SharedDomainTreeContext =
  createContext<SharedDomainTreeContextValue | null>(null);

const noopGenerateUrlPatterns = (): string[] | undefined => undefined;
const noopRefetch = (): void => {
  return;
};

const NONE_DOMAIN_TREE_FILTERING: SharedDomainTreeContextValue = {
  domainTree: null,
  generateUrlPatterns: noopGenerateUrlPatterns,
  isLoading: false,
  isFetching: false,
  refetch: noopRefetch,
};

export type DomainTreeFilteringScope = 'application' | 'none';

type SharedDomainTreeProviderProps = {
  activeTabType: MainContentTab | undefined;
  assetTypeFilter: AssetType | undefined;
  children: ReactNode;
};

export function SharedDomainTreeProvider({
  activeTabType,
  assetTypeFilter,
  children,
}: SharedDomainTreeProviderProps) {
  const appTargetId = useAppTargetId();
  const isDomainTreeTab = isDomainTreeFilterTab(activeTabType);
  const shouldApplyAppLevel = activeTabType === 'signals-list';

  const domainTreeQueryOptions = useMemo(
    () =>
      resolveActiveTabDomainTreeQueryOptions({
        activeTabType,
        assetTypeFilter,
      }),
    [activeTabType, assetTypeFilter],
  );

  const { domainTree, generateUrlPatterns, isLoading, isFetching, refetch } =
    useApplicationDomainTree({
      applicationId: appTargetId,
      options: {
        ...domainTreeQueryOptions,
        applyApplicationLevelRoot: shouldApplyAppLevel,
        enabled: Boolean(appTargetId) && isDomainTreeTab,
      },
    });

  const value = useMemo(
    () => ({
      domainTree,
      generateUrlPatterns,
      isLoading,
      isFetching,
      refetch,
    }),
    [domainTree, generateUrlPatterns, isFetching, isLoading, refetch],
  );

  return (
    <SharedDomainTreeContext.Provider value={value}>
      {children}
    </SharedDomainTreeContext.Provider>
  );
}

export function useDomainTreeFiltering(
  scope: DomainTreeFilteringScope,
): SharedDomainTreeContextValue {
  const sharedDomainTree = useContext(SharedDomainTreeContext);

  if (scope === 'none' || !sharedDomainTree) {
    return NONE_DOMAIN_TREE_FILTERING;
  }

  return sharedDomainTree;
}

export function useRequiredDomainTree(): SharedDomainTreeContextValue {
  const sharedDomainTree = useContext(SharedDomainTreeContext);

  if (!sharedDomainTree) {
    throw new Error(
      'useRequiredDomainTree must be used within SharedDomainTreeProvider',
    );
  }

  return sharedDomainTree;
}

export function useTabDomainTreeFiltering({
  scope,
  selectedNodes,
}: {
  scope: DomainTreeFilteringScope;
  selectedNodes: string[];
}) {
  const { domainTree, generateUrlPatterns } = useDomainTreeFiltering(scope);
  const effectiveSelectedNodes = scope === 'none' ? [] : selectedNodes;

  return useTreeFilterParams(
    effectiveSelectedNodes,
    domainTree,
    generateUrlPatterns,
  );
}
